import { useMemo, useCallback } from "react"
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  ReferenceLine,
  LabelList,
  Rectangle,
} from "recharts"
import { Card } from "../../common"
import { formatNumber } from "~/ui-rtk/utils/format"

// Custom rounded bar shape with all corners rounded
const RoundedBarShape = (props: {
  x?: number
  y?: number
  width?: number
  height?: number
  fill?: string
}) => {
  const { x = 0, y = 0, width = 0, height = 0, fill } = props
  if (!height || height <= 0) return null

  return (
    <Rectangle
      x={x}
      y={y}
      width={width}
      height={height}
      fill={fill}
      stroke={fill}
      strokeWidth={2}
      radius={[4, 4, 4, 4]}
    />
  )
}

export type WaterfallDataPoint = {
  name: string
  value: number
  color?: string
  isTotal?: boolean
}

export type WaterfallChartProps = {
  data: WaterfallDataPoint[]
  height?: number | string
  showGrid?: boolean
  showTooltip?: boolean
  showLabels?: boolean
  colors?: {
    positive: string
    negative: string
    total: string
  }
  formatValue?: (value: number) => string
  className?: string
}

type ProcessedDataPoint = {
  name: string
  value: number
  range: [number, number]
  color: string
  isTotal: boolean
  displayValue: number
  labelValue: string
}

const defaultColors = {
  positive: "#66DECD", // Teal for positive/total values (matching Marketing Trends)
  negative: "#FF6B6B", // Red for negative values (spending/costs)
  total: "#66DECD", // Teal for totals (matching Total Revenue)
}

/**
 * WaterfallChart - A ranged bar chart that shows cumulative changes
 *
 * Each bar represents a change from the previous cumulative value.
 * Positive changes go up, negative changes go down.
 * "Total" bars show the cumulative sum from zero.
 */
export default function WaterfallChart({
  data,
  height = 400,
  showGrid = true,
  showTooltip = true,
  showLabels = true,
  colors = defaultColors,
  formatValue: customFormatValue,
  className,
}: WaterfallChartProps) {
  const defaultFormatter = useCallback(
    (value: number) =>
      formatNumber(Math.abs(value), { compact: true, currency: true }),
    [],
  )

  const valueFormatter = customFormatValue || defaultFormatter

  // Process data to create ranged values for the waterfall effect
  // Example: Revenue=200, Cost1=-20, Cost2=-70, NetProfit (total)
  // Ranges: [0,200], [180,200], [110,180], [0,110]
  const processedData = useMemo<ProcessedDataPoint[]>(() => {
    let cumulative = 0

    return data.map((item, index) => {
      if (item.isTotal) {
        // For the first item (starting total), use the item's value
        // For subsequent totals, use the cumulative value
        const totalValue = index === 0 ? item.value : cumulative

        // If this is the first item, set cumulative to its value
        if (index === 0) {
          cumulative = item.value
        }

        // Total bars start from 0 and go to the total value
        const range: [number, number] = [0, totalValue]
        return {
          name: item.name,
          value: item.value,
          range,
          color: item.color || colors.total,
          isTotal: true,
          displayValue: totalValue,
          labelValue: valueFormatter(totalValue),
        }
      }

      // For change bars (costs/additions)
      const previousCumulative = cumulative
      cumulative += item.value // item.value is negative for costs
      const newCumulative = cumulative

      // Range should be [lower, higher] for the bar to render correctly
      // For negative values (costs): range is [newCumulative, previousCumulative]
      // This creates a "floating" bar from the new lower value up to the previous higher value
      const range: [number, number] =
        item.value < 0
          ? [newCumulative, previousCumulative] // e.g., [180, 200] for a -20 cost
          : [previousCumulative, newCumulative] // e.g., [100, 150] for a +50 gain

      return {
        name: item.name,
        value: item.value,
        range,
        color:
          item.color || (item.value >= 0 ? colors.positive : colors.negative),
        isTotal: false,
        displayValue: Math.abs(item.value),
        labelValue: valueFormatter(Math.abs(item.value)),
      }
    })
  }, [data, colors, valueFormatter])

  // Calculate Y-axis domain
  const yDomain = useMemo(() => {
    let min = 0
    let max = 0
    let cumulative = 0

    data.forEach((item, index) => {
      if (item.isTotal && index === 0) {
        // First total sets the starting cumulative
        cumulative = item.value
      } else if (!item.isTotal) {
        cumulative += item.value
      }
      min = Math.min(min, cumulative, 0)
      max = Math.max(max, cumulative)
    })

    // Add some padding at the top for labels
    const padding = (max - min) * 0.15
    return [0, Math.ceil(max + padding)]
  }, [data])

  return (
    <div className={className} style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={processedData}
          margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
        >
          {showGrid && (
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="#303367"
              vertical={false}
            />
          )}
          <XAxis
            dataKey="name"
            stroke="#667085"
            fontSize={11}
            tickLine={false}
            axisLine={{ stroke: "#303367" }}
            interval={0}
            tick={{ fill: "#9CA3AF" }}
          />
          <YAxis
            stroke="#667085"
            fontSize={11}
            tickLine={false}
            axisLine={false}
            domain={yDomain}
            tickFormatter={v => formatNumber(v, { compact: true })}
            tick={{ fill: "#9CA3AF" }}
          />
          <ReferenceLine y={0} stroke="#303367" />
          {showTooltip && (
            <Tooltip
              cursor={{ fill: "rgba(165, 124, 251, 0.1)" }}
              content={({ active, payload }) => {
                if (active && payload && payload.length) {
                  const dataPoint = payload[0].payload as ProcessedDataPoint
                  return (
                    <Card
                      rounded
                      className="bg-basic-black border border-basic-blue p-3"
                    >
                      <p className="text-white font-medium mb-1">
                        {dataPoint.name}
                      </p>
                      <p className="text-white/80">
                        {dataPoint.isTotal ? "Total: " : "Change: "}
                        <span
                          style={{ color: dataPoint.color }}
                          className="font-semibold"
                        >
                          {!dataPoint.isTotal && dataPoint.displayValue < 0
                            ? "-"
                            : ""}
                          {dataPoint.labelValue}
                        </span>
                      </p>
                    </Card>
                  )
                }
                return null
              }}
            />
          )}
          {/* Ranged bar - each bar uses [start, end] values */}
          <Bar dataKey="range" barSize={30} shape={<RoundedBarShape />}>
            {processedData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.color} />
            ))}
            {showLabels && (
              <LabelList
                dataKey="labelValue"
                position="top"
                fill="#FFFFFF"
                fontSize={11}
                fontWeight={500}
                offset={8}
              />
            )}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  )
}
